// @flow
import Colors from '../assets/colors.json'
import IconAllGood from 'material-ui/svg-icons/action/check-circle'
import IconError from 'material-ui/svg-icons/alert/error'
import IconGen1 from '../assets/icons/Device/gen1.svg'
import IconGen2 from '../assets/icons/Device/gen2.svg'
import IconWaiting from 'material-ui/svg-icons/av/av-timer'
import IconWarning from 'material-ui/svg-icons/alert/warning'
import moment from 'moment-timezone'

// types
import type {
  FlexNode,
  ModelId,
  State,
  Type as DeviceType,
  LinkedSensor,
  ControllerState
} from '../ducks/device/types'
import type { DeviceInfo } from '../ducks/location/types'

/**
 * Utility functions related to devices
 * @module util/device
 */

// Type Definitions ///////////////////////////////////////////////////////////

/**
 * @typedef {object} StateObject
 * @property {string} text - Display text
 * @property {string} color - Associated color
 * @property {string} id - ID of state
 * @property {string} img - Associated image
 */

// States /////////////////////////////////////////////////////////////////////

export type StateObject = {|
  text: string,
  id: State
|}

/**
 * Map of all possible state objects
 * @type {Map}
 * @const
 */
const states: Map<State, StateObject> = new Map([
  [
    'STANDBY',
    {
      text: 'Standby Mode',
      id: 'STANDBY'
    }
  ],
  [
    'IDLE',
    {
      text: 'Online',
      id: 'IDLE'
    }
  ],
  [
    'DELAY',
    {
      text: 'Watering Delay',
      id: 'DELAY'
    }
  ],
  [
    'PAUSED',
    {
      text: 'Watering Paused',
      id: 'PAUSED'
    }
  ],
  [
    'WATERING',
    {
      text: 'Watering',
      id: 'WATERING'
    }
  ],
  [
    'UNBORN',
    {
      text: 'Offline',
      id: 'UNBORN'
    }
  ],
  [
    'STARTUP',
    {
      text: 'Offline',
      id: 'STARTUP'
    }
  ],
  [
    'UNKNOWN',
    {
      text: 'Offline',
      id: 'UNKNOWN'
    }
  ],
  [
    'OFFLINE',
    {
      text: 'Offline',
      id: 'OFFLINE'
    }
  ],
  [
    'EXTENDED_OFFLINE',
    {
      text: 'Offline',
      id: 'OFFLINE'
    }
  ],
  [
    'PROVISIONING',
    {
      text: 'Offline',
      id: 'OFFLINE'
    }
  ]
])

/**
 * Get state object
 * @function
 * @param  {string} stateId - State ID
 * @return {[object]} - State object
 */
export const getState = (stateId: State): StateObject =>
  // $FlowFixMe
  states.get(stateId)

/**
 * Build State/Status Text
 * @param  {object} deviceStateObj - Device state
 * @param  {string} timeZone - Location time zone
 * @return {string} - Status text
 */
export const buildStateText = (
  deviceStateObj: Object,
  timeZone: string
): string => {
  if (displayRainSensorTripped(deviceStateObj)) {
    return 'Rain Sensor On'
  }

  const state: State = deviceStateObj.state || 'UNKNOWN'
  let str = null

  if (state) {
    str = getState(state).text
    if (state === 'DELAY' && deviceStateObj.rainDelayExpiration) {
      str = getCustomState({
        rainDelayExpiration: deviceStateObj.rainDelayExpiration,
        state: 'DELAY',
        timeZone: timeZone
      }).text
    }
  }

  return str || ''
}

/**
 * determine if rain sensor is tripped and has display priority
 * @param {Object} deviceState - state object
 * @returns {bool} should show rain sensor tripped data
 */
export const displayRainSensorTripped = ({
  rainSensorTripped,
  state
}: ControllerState) => {
  const stateId = getState(state).id
  return (
    !['STANDBY', 'WATERING', 'OFFLINE'].includes(stateId) && rainSensorTripped
  )
}

/**
 * @summary Get custom device state object
 * @description Creates custom `text` property for device state if the device
 *              is in a watering delay state (adds end time)
 * @param {Object} deviceState - Object containing required data (state id, timeZone, delay expiration)
 * @returns {Object} - StateObject with (possibly) customized text
 */
export const getCustomState = (deviceState: {
  state: State,
  timeZone: string,
  rainDelayExpiration: ?string
}): StateObject => {
  const state: Object = Object.assign({}, getState(deviceState.state))

  // Update state text if neccessary
  if (state && state.id === 'DELAY') {
    state.text =
      'Watering Delay until ' +
      moment(deviceState.rainDelayExpiration)
        .tz(deviceState.timeZone)
        .format('MM/DD')
  }

  return state
}

/**
 * Get an array of all state ids
 * @return {string[]} - Array of state ids
 */
export const getStateIds = (): Array<State> => Array.from(states.keys())

/**
 * Get an array of all state objects
 * @return {object[]} - Array of state objects
 */
export const getStateValues = (): Array<StateObject> =>
  Array.from(states.values())

// Statuses ///////////////////////////////////////////////////////////////////

export type StatusId = 'GOOD' | 'WARNING' | 'ERROR'
export type StatusObject = {|
  img: any,
  text: string,
  color: string,
  id: State
|}

/**
 * Map of all possible status objects
 * @type {map}
 * @const
 */
const statuses = new Map([
  [
    'GOOD',
    {
      img: IconAllGood,
      text: 'All Good',
      color: Colors.white,
      id: 'GOOD'
    }
  ],
  [
    'WARNING',
    {
      img: IconWarning,
      text: 'Warning',
      color: Colors.white,
      id: 'WARNING'
    }
  ],
  [
    'ERROR',
    {
      img: IconError,
      text: 'Error',
      color: Colors.white,
      id: 'ERROR'
    }
  ],
  [
    'NONE',
    {
      img: IconWaiting,
      text: 'loading',
      color: Colors.white,
      id: 'NONE'
    }
  ]
])

/**
 * Get status object
 * @function
 * @param  {('GOOD'|'WARNING'|'ERROR')} statusId - Status ID
 * @return {StateObject} - State object
 */
export const getStatus = (statusId: StatusId) => statuses.get(statusId)

/**
 * Get an array of all status ids
 * @return {string[]} - Array of status ids
 */
export const getStatusIds = () => {
  // $FlowFixMe
  return Array.from(statuses.keys())
}

export const getStatusValues = () => {
  // $FlowFixMe
  return Array.from(statuses.values())
}

// Device Types ///////////////////////////////////////////////////////////////

/**
 * @typedef {object} - DeviceTypeObject
 * @property {string} id - Device type ID; eg: 'CONTROLLER_GEN2'
 * @property {string} text - Displayable text for device type
 */

export type TypeObject = {|
  +text: string,
  +id: DeviceType,
  +icon?: string
|}

/**
 * Map of all possible device types
 * @type {map}
 * @const
 */
const types: Map<DeviceType, TypeObject> = new Map([
  [
    'CONTROLLER_GEN1',
    {
      id: 'CONTROLLER_GEN1',
      text: 'Rachio Gen 1',
      icon: IconGen1
    }
  ],
  [
    'CONTROLLER_GEN2',
    {
      id: 'CONTROLLER_GEN2',
      text: 'Rachio Gen 2',
      icon: IconGen2
    }
  ],
  [
    'CONTROLLER_GEN3',
    {
      id: 'CONTROLLER_GEN3',
      text: 'Rachio Gen 3',
      icon: IconGen2
    }
  ],
  [
    'WEATHER_STATION_VIRTUAL',
    {
      id: 'WEATHER_STATION_VIRTUAL',
      text: 'Rachio Virtual Weather Station'
    }
  ]
])

/**
 * Get device type information
 * @param  {string} typeId - Device type id
 * @return {DeviceTypeObject} - Information about device type
 */
export const getType = (typeId: DeviceType) => types.get(typeId)

/**
 * Given a device, determine if it's an irrigation controller
 * @param  {object} deviceObj - Device
 * @return {boolean} - Whether or not device is an irrigation controller
 */
export const deviceIsIrrigationController = (deviceObj: {
  type: DeviceType
}) => {
  return (
    deviceObj &&
    (deviceObj.type === 'CONTROLLER_GEN1' ||
      deviceObj.type === 'CONTROLLER_GEN2' ||
      deviceObj.type === 'CONTROLLER_GEN3')
  )
}

/**
 * Given a device, determine if it's a weather station
 * @param  {object} deviceObj - Device
 * @return {boolean} - Whether or not device is a weather station
 */
export const deviceIsWeatherStation = (deviceObj: DeviceInfo) => {
  return (
    deviceObj &&
    deviceObj.type &&
    deviceObj.type.substr(0, 15) === 'WEATHER_STATION'
  )
}

/**
 * Given a device state, get the current schedule
 * @param {object} deviceState - Device state
 * @returns {(object|null)} - Current schedule in device state
 */
export const getCurrentScheduleInDeviceState = (
  deviceState: ?ControllerState
) => {
  return deviceState &&
    deviceState.correctSchedule === true &&
    deviceState.currentRunningSchedule
    ? deviceState.currentRunningSchedule
    : null
}

/**
 * Given two possible sensors, determine if one is an ENABLED flow sensor
 * @param  {LinkedSensor} sensor1 -sensor1
 * @param  {LinkedSensor} sensor2 - sensor2
 * @return {boolean} is one an enabled flow sensor
 */
export const isOneEnabledFlowSensor = (
  sensor1: ?LinkedSensor,
  sensor2: ?LinkedSensor
) => sensorIsEnabledFlowSensor(sensor1) || sensorIsEnabledFlowSensor(sensor2)

/**
 * Given a sensor, determine if it's an ENABLED flow sensor
 * @param  {LinkedSensor} sensor -
 * @return {boolean} is an enabled flow sensor
 */
export const sensorIsEnabledFlowSensor = (sensor: ?LinkedSensor) => {
  return Boolean(sensor && sensor.enabled && sensor.sensorType === 'FLOW')
}

// Models /////////////////////////////////////////////////////////////////////

type ModelObject = {
  +id: ModelId,
  +type: DeviceType,
  +meta: Object
}

const models: Map<ModelId, ModelObject> = new Map([
  [
    'GENERATION1_8ZONE',
    {
      id: 'GENERATION1_8ZONE',
      type: 'CONTROLLER_GEN1',
      meta: {
        numberOfZones: 8
      }
    }
  ],
  [
    'GENERATION1_16ZONE',
    {
      id: 'GENERATION1_16ZONE',
      type: 'CONTROLLER_GEN1',
      meta: {
        numberOfZones: 16
      }
    }
  ],
  [
    'GENERATION2_8ZONE',
    {
      id: 'GENERATION2_8ZONE',
      type: 'CONTROLLER_GEN2',
      meta: {
        numberOfZones: 8
      }
    }
  ],
  [
    'GENERATION2_16ZONE',
    {
      id: 'GENERATION2_16ZONE',
      type: 'CONTROLLER_GEN2',
      meta: {
        numberOfZones: 16
      }
    }
  ]
])

/**
 * Get device model information
 * @param  {string} modelId - Device model id
 * @return {object} - Information about device model
 */
export const getModel = (modelId: ModelId) => models.get(modelId)

// Util ///////////////////////////////////////////////////////////////////////

/**
 * Determine whether or not a device should be considered 'online' for
 * things like sending manual zone runs, creating schedules, etc.
 * @param  {string} state - Device state
 * @return {boolean} - Whether or not the device should be considered online
 */
export const deviceIsOnline = (state: State): boolean => {
  const offlineStates = ['OFFLINE', 'EXTENDED_OFFLINE', 'PROVISIONING']
  const isOnline = !offlineStates.includes(state)
  return isOnline
}

/**
 * Determine whether or not a device should be considered 'online' for
 * things like sending manual zone runs, creating schedules, etc.
 * @param  {string} id - wireless flow sensor id
 * @param  {array} flexNodes - array of device's flexNodes
 * @return {boolean} - Whether or not the device should be considered online
 */
export const wirelessFlowSensorIsConnected = (
  id: string,
  flexNodes: Array<FlexNode>
): boolean => {
  const node = (flexNodes || []).find(node => node.deviceId === id)

  if (!node) return false

  return node.paired
}

/**
 * Format string for MAC Address
 * @param {string} macAddress MAC Address
 * @return {string} Formatted string
 */
export const createMacAddressStr = (macAddress: string) => {
  const splitArray: Array<string> = []
  const split = (char: string, idx: number, arr: Array<string>) => {
    if (idx % 2 === 0) {
      splitArray[idx] = char
    } else {
      splitArray[idx - 1] = splitArray[idx - 1] + char
      if (idx < arr.length - 1) {
        splitArray[idx - 1] = splitArray[idx - 1] + ' : '
      }
    }
  }
  Array.from(macAddress).forEach(split)

  return splitArray.join().replace(/,/g, '')
}

// Irrigation Controllers /////////////////////////////////////////////////////

/**
 * Determine how many schedules can fit on a controller
 * @param {string} deviceType - Device type
 * @returns {number} - Max number of schedules the device can hold (defaults to the limit for gen 2; 16)
 */
export const getScheduleLimit = (deviceType: ?DeviceType): 12 | 16 => {
  return deviceType === 'CONTROLLER_GEN1' ? 12 : 16
}
